import { auth, db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  addDoc,
  writeBatch,
  deleteDoc,
  onSnapshot, // ensure this is imported if you use listeners
  query,      // FIX: missing import caused "query is not defined"
  orderBy,    // if you sort in listenBinders/listenBinderSlots
  updateDoc,
  where,
  limit,
} from "firebase/firestore";
import type { Unsubscribe } from "firebase/firestore";
import type { Binder, BinderSlot } from "@/types/binders";

/* =====================================
   UID helper
===================================== */
function requireUid(): string {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in first.");
  return uid;
}

/* =====================================
   Collection/doc refs
===================================== */
export function bindersCol(uid = requireUid()) {
  return collection(db, "users", uid, "binders");
}
export function binderDoc(binderId: string, uid = requireUid()) {
  return doc(db, "users", uid, "binders", binderId);
}
export function binderSlotsCol(binderId: string, uid = requireUid()) {
  return collection(db, "users", uid, "binders", binderId, "slots");
}

/* =====================================
   Listeners
===================================== */
export function listenBinders(
  cb: (binders: Binder[]) => void,
  uid = requireUid(),
  onError?: (e: any) => void,
): Unsubscribe {
  const qy = query(bindersCol(uid), orderBy("position", "asc"));
  return onSnapshot(
    qy,
    (snap) => {
      const out: Binder[] = [];
      snap.forEach((d) => out.push({ id: d.id, ...(d.data() as any) }));
      cb(out);
    },
    (err) => onError?.(err),
  );
}

export function listenBinderSlots(
  binderId: string,
  cb: (slots: BinderSlot[]) => void,
  uid = requireUid(),
  onError?: (e: any) => void,
): Unsubscribe {
  const qy = query(binderSlotsCol(binderId, uid), orderBy("index", "asc"));
  return onSnapshot(
    qy,
    (snap) => {
      const out: BinderSlot[] = [];
      snap.forEach((d) => out.push({ id: d.id, ...(d.data() as any) }));
      cb(out);
    },
    (err) => onError?.(err),
  );
}

// DEPRECATED: this previously read binders/{binderId}/cards; slots are the new source of truth.
export function listenBinderCards(
  binderId: string,
  onCards: (cards: any[]) => void,
  uid = requireUid(),
): Unsubscribe {
  const ref = collection(db, "users", uid, "binders", binderId, "slots");
  return onSnapshot(ref, (snap) => {
    const rows: any[] = [];
    snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
    onCards(rows);
  });
}

/* =====================================
   Mutations
===================================== */
export async function createBinder(name: string, uid = requireUid()): Promise<string> {
  const last = await getDocs(query(bindersCol(uid), orderBy("position", "desc"), limit(1)));
  const nextPos = last.docs.length ? ((last.docs[0].data().position as number) || 0) + 1 : 0;
  const ref = await addDoc(bindersCol(uid), {
    name: name || "Binder",
    position: nextPos,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function renameBinder(binderId: string, name: string, uid = requireUid()) {
  await updateDoc(binderDoc(binderId, uid), { name, updatedAt: serverTimestamp() });
}

export async function setBinderPosition(binderId: string, position: number, uid = requireUid()) {
  await updateDoc(binderDoc(binderId, uid), { position, updatedAt: serverTimestamp() });
}

// ADOPTED: slots-based add; picks first free index unless targetIndex provided.
export async function addCardToBinder(
  binderId: string,
  userCardId: string,
  uid: string,
  opts?: { targetIndex?: number },
): Promise<{ slotId: string; index: number }> {
  if (!uid || !binderId || !userCardId) {
    throw new Error("addCardToBinder: missing uid, binderId, or userCardId");
  }
  const slotsCol = collection(db, "users", uid, "binders", binderId, "slots");

  // Find first free index (or use provided targetIndex)
  const snap = await getDocs(query(slotsCol, orderBy("index", "asc")));
  const used = new Set<number>();
  snap.docs.forEach((d) => {
    const v = d.data() as any;
    if (typeof v?.index === "number") used.add(v.index);
  });

  let index =
    typeof opts?.targetIndex === "number" && opts.targetIndex >= 0
      ? opts.targetIndex
      : 0;
  while (used.has(index)) index++;

  const newRef = await addDoc(slotsCol, {
    userCardId,
    index,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { slotId: newRef.id, index };
}

export async function moveCardBetweenBinders(
  fromBinderId: string,
  toBinderId: string,
  userCardId: string,
  uid: string,
  opts?: { targetIndex?: number },
): Promise<void> {
  if (fromBinderId === toBinderId) {
    // Reposition within same binder: delete existing slot then re-add at targetIndex
    await removeCardFromBinder(fromBinderId, userCardId, uid);
    await addCardToBinder(toBinderId, userCardId, uid, opts);
    return;
  }
  // Add to new binder first
  await addCardToBinder(toBinderId, userCardId, uid, opts);
  // Remove from old binder (best-effort)
  await removeCardFromBinder(fromBinderId, userCardId, uid);
}

export async function removeCardFromBinder(
  binderId: string,
  userCardId: string,
  uid: string,
): Promise<void> {
  const slots = collection(db, "users", uid, "binders", binderId, "slots");
  const snap = await getDocs(query(slots, where("userCardId", "==", userCardId)));
  const batch = writeBatch(db);
  snap.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

// Clean delete a binder: remove all slots so cards return to My Collection
export async function cleanDeleteBinder(binderId: string, uid: string): Promise<void> {
  const slots = collection(db, "users", uid, "binders", binderId, "slots");
  const snap = await getDocs(slots);
  const batch = writeBatch(db);
  snap.forEach((d) => batch.delete(d.ref));
  batch.delete(doc(db, "users", uid, "binders", binderId));
  await batch.commit();
}

/**
 * Delete a binder and all of its slots, and clear binder linkage on user cards.
 * After this runs, cards reappear in the Collection view (listenAssignedCardIds will no longer see them).
 */
export async function deleteBinderAndSlots(binderId: string, uid: string): Promise<void> {
  const binderRef = doc(db, "users", uid, "binders", binderId);
  const slotsCol = collection(db, "users", uid, "binders", binderId, "slots");

  // Collect all slots and their userCardIds
  const slotsSnap = await getDocs(slotsCol);
  const slotDocs = slotsSnap.docs;

  // Chunk into batches of 500 writes
  const CHUNK = 450; // leave headroom
  for (let i = 0; i < slotDocs.length; i += CHUNK) {
    const batch = writeBatch(db);
    const chunk = slotDocs.slice(i, i + CHUNK);

    for (const s of chunk) {
      // Delete the slot itself
      batch.delete(doc(db, "users", uid, "binders", binderId, "slots", s.id));
    }

    // Optionally, mark binder meta as cleared in this chunk (not required, binder will be deleted after)
    batch.set(
      binderRef,
      { cardCount: 0, nextIndex: 0, updatedAt: serverTimestamp() },
      { merge: true },
    );

    await batch.commit();
  }

  // Final batch to delete the binder document
  const finalBatch = writeBatch(db);
  finalBatch.delete(binderRef);
  await finalBatch.commit();
}

/* =====================================
   Assigned card ids across binders
===================================== */
// Single exported function (backward-compatible: accepts uid or just cb)
export function listenAssignedCardIds(
  arg1: string | ((assigned: Set<string>) => void),
  arg2?: ((assigned: Set<string>) => void) | ((e: any) => void),
  arg3?: (e: any) => void,
): Unsubscribe {
  if (typeof arg1 === "string") {
    const uid = arg1;
    const cb = arg2 as (assigned: Set<string>) => void;
    const onError = arg3;
    return listenAssignedCardIdsFor(uid, cb, onError);
  }
  const cb = arg1 as (assigned: Set<string>) => void;
  const onError = arg2 as ((e: any) => void) | undefined;
  return listenAssignedCardIdsFor(requireUid(), cb, onError);
}

// Internal (not exported): watch assigned card ids for a specific uid
function listenAssignedCardIdsFor(
  uid: string,
  cb: (assigned: Set<string>) => void,
  onError?: (e: any) => void,
): Unsubscribe {
  const perBinder = new Map<string, Set<string>>();
  const slotUnsubs = new Map<string, Unsubscribe>();

  function emit() {
    const all = new Set<string>();
    for (const set of perBinder.values()) for (const id of set) all.add(id);
    cb(all);
  }
  function watchSlots(binderId: string) {
    return listenBinderSlots(
      binderId,
      (slots) => {
        const ids = new Set<string>();
        for (const s of slots) if ((s as any)?.userCardId) ids.add(String((s as any).userCardId));
        perBinder.set(binderId, ids);
        emit();
      },
      uid,
      onError,
    );
  }

  const unbinders = listenBinders(
    (binders) => {
      const ids = new Set(binders.map((b) => b.id));
      // remove old watchers
      for (const [bId, off] of slotUnsubs) {
        if (!ids.has(bId)) {
          try { off(); } catch {}
          slotUnsubs.delete(bId);
          perBinder.delete(bId);
        }
      }
      // add new watchers
      for (const b of binders) {
        if (!slotUnsubs.has(b.id)) slotUnsubs.set(b.id, watchSlots(b.id));
      }
      emit();
    },
    uid,
    onError,
  );

  return () => {
    try { unbinders(); } catch {}
    for (const [, off] of slotUnsubs) try { off(); } catch {}
    slotUnsubs.clear();
    perBinder.clear();
  };
}
